<script lang="ts">
	// Design system components
	import {
		CardGrid,
		Collapse,
		Container,
		Input,
		SelectCard,
		Stack,
		Text,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import { getRelayUsage } from 'src/helpers/device_settings_helpers';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { generateURI, parseID } from '../helpers';
	import { filterDevicesByDeviceName } from 'src/components/Lists/list-helpers';
	import {
		deriveRoomName,
		HiddenDevicesRoomID,
		SharedRecentlyDevicesRoomID,
	} from 'src/helpers/service_rooms_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	// Stores
	import { createMemberSettingsSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	// Types
	import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	// Svelte hooks
	import { onMount } from 'svelte';
	// Services
	import { format } from 'svelte-i18n';
	import NoSearchResults from 'src/components/Lists/NoSearchResults.svelte';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let editableObject;
	let actuatorID = parseID(editableObject.thermostat.actuator);

	let deviceFilter = '';
	let editGroup = { devices: [actuatorID] };

	let listDevByGroupType = [];

	let allItemsRefs: { [key: string | number]: HTMLElement } = {};

	const cachedCurrentGroupDevices = editGroup.devices;

	let mappedListDevicesByRoomID: {
		roomID?: string | number;
		roomName?: String;
		devices?: {
			name: string;
			id: string | number;
			image?: string;
			backgroundColor?: AvatarColorVariantTypes;
			icon?: string;
			selected: boolean;
			roomName: string;
			roomID: string | number;
		}[];
	}[] = [];

	$: memberSettingsStore = createMemberSettingsSnapshot(
		Object.values($devices).map((d: Device) => d.id),
	);
	$: memberSettings = $memberSettingsStore;

	$: listDevByGroupType = [
		...Object.values($devices).filter((d: Device) => {
			return (
				!d.shared &&
				(isTestUser() || d.room_id !== HiddenDevicesRoomID) &&
				d.room_id != (`${SharedRecentlyDevicesRoomID}` as any) &&
				typeof memberSettings[real_id(d.id)] !== 'undefined' &&
				(d.category === 'relay' ||
					getRelayUsage(d).usage === 'relay' ||
					// module in 'inputs_reader' and 'shell' mode doesn't have control components
					(d.category === 'module' && !/^(inputs_reader|shell)$/.test(d.mode)) ||
					(d.category === 'emeter' &&
						d.mode === 'emeter' &&
						Object.keys(memberSettings[real_id(d.id)] ?? {}).some(
							(key) => key.startsWith('switch') || key.startsWith('relay'),
						) &&
						(!d.id.includes('_') || d.channel === 0)))
			);
		}),
	];

	let roomsExpanded = {};

	$: mappedListDevicesByRoomID = filterDevicesByRoomID(
		filterDevicesByDeviceName(listDevByGroupType, deviceFilter),
	);

	onMount(() => {
		roomsExpanded = mappedListDevicesByRoomID.reduce(
			(o, room) => ({
				...o,
				[room.roomID]: room.devices.some((device) => device.selected),
			}),
			{},
		);
	});

	const filterDevicesByRoomID = (array: Device[]) => {
		let filteredDevicesByRoom = {};

		const deletedDevices = cachedCurrentGroupDevices.filter(
			(dId) => 'undefined' === typeof $devices[dId],
		);

		array.forEach((device: Device, i) => {
			const roomID = device.room_id;

			const deviceData = {
				name: device.name,
				id: device.id,
				selected: editGroup.devices.findIndex((el) => el === device.id) !== -1,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: device.image,
				backgroundColor: device.backgroundColor,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID].devices = [
					...filteredDevicesByRoom[roomID].devices,
					deviceData,
				];
			} else {
				filteredDevicesByRoom[roomID] = {
					devices: [deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
				};
			}
		});

		if (deletedDevices.length) {
			deletedDevices.forEach((id) => {
				const deviceData = {
					name: `deleted: ${id}`,
					id,
					selected: editGroup.devices.findIndex((el) => el === id) !== -1,
					roomName: capFirstChar($format('deleted_devices', { default: 'deleted devices' })),
					roomID: -99,
					icon: 'fas fa-trash',
				};

				if (filteredDevicesByRoom[-99]) {
					filteredDevicesByRoom[-99].devices = [
						...filteredDevicesByRoom[-99].devices,
						deviceData,
					];
				} else {
					filteredDevicesByRoom[-99] = {
						devices: [deviceData],
						roomName: capFirstChar(
							$format('deleted_devices', {
								default: 'deleted devices',
							}),
						),
						roomID: -99,
					};
				}
			});
		}
		return Object.values(filteredDevicesByRoom);
	};

	const onSelectDevice = (device) => {
		const selectedDeviceId = device.id;
		const currenDeviceRoomID = device.roomID;
		mappedListDevicesByRoomID = mappedListDevicesByRoomID.map((room: any) => {
			if (room.roomID === currenDeviceRoomID) {
				return {
					...room,
					devices: room.devices.map((d) => {
						if (d.id === selectedDeviceId) {
							return {
								...d,
								selected: !d.selected,
							};
						} else {
							return { ...d, selected: false };
						}
					}),
				};
			} else {
				room.devices.map((d) => (d.selected = false));
			}

			return room;
		});
		editableObject.thermostat.actuator = generateURI(
			editableObject.thermostat.actuator,
			selectedDeviceId,
		);
	};
</script>

<Container padding="scrollPaddings">
	<Stack
		gap="s"
		class="mb-l"
		direction="column"
		directionMobile="row"
		align="between"
		alignMobile="none"
	>
		<Text element="h2" noWrap class="align-self-center"
			>{$format('select_thermostat_actuator', {
				default: 'Select actuator',
			})}</Text
		>
		<Input
			type="text"
			label={capFirstChar($format('search'))}
			icon="fas fa-search"
			class="width-lg-l width-sm-full"
			bind:value={deviceFilter}
			id="input-nbbciz6"
			aria-labelledby="input-nbbciz6"
		/>
	</Stack>

	{#if mappedListDevicesByRoomID.length > 0}
		{#each mappedListDevicesByRoomID as room}
			<Collapse
				id="collapse-1r02dv0"
				class="mb-s"
				title={'' + room.roomName}
				expanded={roomsExpanded[room.roomID]}
				on:click={() => {
					roomsExpanded[room.roomID] = !roomsExpanded[room.roomID];
				}}
			>
				<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
					{#each room.devices as device (device.id)}
						<div class="d-flex" bind:this={allItemsRefs[device.id]}>
							<SelectCard
								cardBgColor={$showNewLayout ? 'color-2' : undefined}
								class="width-full"
								type="horizontal"
								title={device.name}
								variant={'checkbox'}
								image={device.image
									? load_image_from(device.image, device?.server)
									: undefined}
								backgroundColor={device.backgroundColor}
								selected={device.selected}
								on:click={() => onSelectDevice(device)}
								icon={device.icon ? device.icon : undefined}
							/>
						</div>
					{/each}
				</CardGrid>
			</Collapse>
		{/each}
	{:else}
		<NoSearchResults search={deviceFilter} />
	{/if}
</Container>
